import React, { useState, useRef, useEffect } from 'react';
import { X, Lock, Check, KeyRound, ChevronLeft } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { useTranslation } from '../contexts/TranslationContext';

interface ParentalPinProps {
  mode: 'verify' | 'setup' | 'change';
  onSuccess: () => void;
  onCancel?: () => void;
  currentPinHash?: string | null;
  onPinSet?: (pin: string) => Promise<void>;
  /** If true, the cancel button is hidden and the modal cannot be dismissed */
  required?: boolean;
  /** Custom title for the modal */
  title?: string;
  /** Custom subtitle/description */
  subtitle?: string;
  /** User email for PIN reset functionality */
  userEmail?: string;
  /** Auth provider (google, apple, email) - if OAuth, use re-auth instead of password */
  authProvider?: string;
}

// Simple hash function for PIN (not cryptographically secure, but sufficient for parental gate)
const hashPin = (pin: string): string => {
  let hash = 0;
  for (let i = 0; i < pin.length; i++) {
    const char = pin.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString(16);
};

export const verifyPin = (pin: string, hash: string): boolean => {
  return hashPin(pin) === hash;
};

export const createPinHash = (pin: string): string => {
  return hashPin(pin);
};

const ParentalPin: React.FC<ParentalPinProps> = ({
  mode,
  onSuccess,
  onCancel,
  currentPinHash,
  onPinSet,
  required = false,
  title: customTitle,
  subtitle: customSubtitle,
  userEmail,
  authProvider
}) => {
  const [pin, setPin] = useState(['', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '']);
  const [step, setStep] = useState<'enter' | 'confirm'>('enter');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPasswordVerification, setShowPasswordVerification] = useState(false);
  const [showOAuthReauth, setShowOAuthReauth] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [verifyingPassword, setVerifyingPassword] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const confirmInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { t } = useTranslation();

  // Check if user is OAuth-only (no password)
  const isOAuthUser = authProvider && authProvider !== 'email';

  // Handle PIN reset - show OAuth re-auth or password verification
  const handleForgotPin = () => {
    setError(null);
    if (isOAuthUser) {
      setShowOAuthReauth(true);
    } else {
      setShowPasswordVerification(true);
    }
  };

  // Handle OAuth re-authentication for PIN reset
  const handleOAuthReauth = async () => {
    setOauthLoading(true);
    setError(null);

    try {
      const provider = authProvider as 'google' | 'apple';
      const { error: authError } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback?pin_reset=true`,
          queryParams: {
            prompt: 'select_account', // Force account selection for re-auth
          }
        }
      });

      if (authError) {
        throw authError;
      }
      // User will be redirected to OAuth provider
    } catch (err: any) {
      console.error('OAuth re-auth error:', err);
      setError(err.message || t("settings.erreur_de_reauthentification", "Re-authentication error"));
      setOauthLoading(false);
    }
  };

  // Verify password to allow PIN reset
  const handlePasswordVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userEmail || !password) return;

    setVerifyingPassword(true);
    setError(null);

    try {
      // Re-authenticate with password
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: userEmail,
        password: password,
      });

      if (authError) {
        setError(t("settings.mot_de_passe_incorrect", "Mot de passe incorrect"));
        setPassword('');
        return;
      }

      // Password verified - allow PIN reset
      setPasswordVerified(true);
      setShowPasswordVerification(false);
      // Reset PIN inputs for new PIN entry
      setPin(['', '', '', '']);
      setStep('enter');
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    } catch (err: any) {
      console.error('Error verifying password:', err);
      setError(err.message || t("settings.erreur_de_verification", "Verification error"));
    } finally {
      setVerifyingPassword(false);
    }
  };

  // Handle password reset redirect
  const handleForgotPassword = async () => {
    if (!userEmail) return;

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(userEmail, {
        redirectTo: `${window.location.origin}/auth/callback`,
      });

      if (error) throw error;

      setError(null);
      alert(t("settings.un_email_de_reinitialisation_a_ete_envoye", "Reset email has been sent"));
    } catch (err: any) {
      setError(err.message || t("pin.error_sending", "Error sending"));
    }
  };

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleDigitChange = (index: number, value: string, isConfirm: boolean = false) => {
    if (!/^\d*$/.test(value)) return;

    const newPin = isConfirm ? [...confirmPin] : [...pin];
    newPin[index] = value.slice(-1);

    if (isConfirm) {
      setConfirmPin(newPin);
    } else {
      setPin(newPin);
    }

    setError(null);

    // Auto-focus next input
    if (value && index < 3) {
      const refs = isConfirm ? confirmInputRefs : inputRefs;
      refs.current[index + 1]?.focus();
    }

    // Check if PIN is complete
    if (value && index === 3) {
      const fullPin = [...newPin.slice(0, 3), value.slice(-1)].join('');
      handlePinComplete(fullPin, isConfirm);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number, isConfirm: boolean = false) => {
    if (e.key === 'Backspace') {
      const currentPin = isConfirm ? confirmPin : pin;
      if (!currentPin[index] && index > 0) {
        const refs = isConfirm ? confirmInputRefs : inputRefs;
        refs.current[index - 1]?.focus();
      }
    }
  };

  const handlePinComplete = async (fullPin: string, isConfirm: boolean) => {
    if (mode === 'verify' && !passwordVerified) {
      // Verify existing PIN (normal flow)
      if (currentPinHash && verifyPin(fullPin, currentPinHash)) {
        onSuccess();
      } else {
        setError(t("settings.code_incorrect", "Code incorrect"));
        setPin(['', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } else if (mode === 'setup' || mode === 'change' || passwordVerified) {
      // Setup new PIN or reset PIN after password verification
      if (!isConfirm) {
        // First entry - ask for confirmation
        setStep('confirm');
        setTimeout(() => {
          confirmInputRefs.current[0]?.focus();
        }, 100);
      } else {
        // Confirmation entry
        const firstPin = pin.join('');
        if (fullPin === firstPin) {
          // PINs match - save
          setLoading(true);
          try {
            if (onPinSet) {
              await onPinSet(createPinHash(fullPin));
            }
            onSuccess();
          } catch (err) {
            setError(t("settings.erreur_lors_de_la_sauvegarde", "Error while saving"));
            setLoading(false);
          }
        } else {
          setError(t("settings.les_codes_ne_correspondent_pas", "Les codes ne correspondent pas"));
          setConfirmPin(['', '', '', '']);
          confirmInputRefs.current[0]?.focus();
        }
      }
    }
  };

  const getTitle = () => {
    if (customTitle) return customTitle;
    if (passwordVerified) return step === 'enter' ? t("settings.nouveau_code_pin", "Nouveau code PIN") : t("settings.confirmer_le_code", "Confirm code");
    if (mode === 'verify') return t("settings.code_parental", "Code parental");
    if (mode === 'setup') return step === 'enter' ? t("settings.creer_un_code_pin", "Create PIN code") : t("settings.confirmer_le_code", "Confirm code");
    if (mode === 'change') return step === 'enter' ? t("settings.nouveau_code_pin", "Nouveau code PIN") : t("settings.confirmer_le_nouveau_code", "Confirmer le nouveau code");
    return t("settings.code_pin", "Code PIN");
  };

  const getSubtitle = () => {
    if (customSubtitle && step === 'enter' && !passwordVerified) return customSubtitle;
    if (passwordVerified && step === 'enter') return t("settings.choisissez_un_nouveau_code_a_4_chiffres", "Choose a new 4-digit code");
    if (mode === 'verify') return t("settings.entrez_le_code_pin_parental_a_4_chiffres", "Enter the 4-digit parental PIN");
    if (step === 'enter') return t("settings.choisissez_un_code_a_4_chiffres", "Choose a 4-digit code");
    return t("settings.entrez_a_nouveau_le_code_pour_confirmer", "Enter code again to confirm");
  };

  const renderPinInputs = (values: string[], refs: React.MutableRefObject<(HTMLInputElement | null)[]>, isConfirm: boolean) => (
    <div className="flex justify-center gap-3">
      {values.map((digit, index) => (
        <input
          key={index}
          ref={(el) => { refs.current[index] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleDigitChange(index, e.target.value, isConfirm)}
          onKeyDown={(e) => handleKeyDown(e, index, isConfirm)}
          disabled={loading}
          className="w-14 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all disabled:opacity-50"
          autoComplete="off"
        />
      ))}
    </div>
  );

  // OAuth re-authentication screen
  if (showOAuthReauth) {
    const providerName = authProvider === 'google' ? 'Google' : authProvider === 'apple' ? 'Apple' : authProvider;
    const providerIcon = authProvider === 'google' ? (
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
    ) : authProvider === 'apple' ? (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
      </svg>
    ) : null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setShowOAuthReauth(false);
                  setError(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors -ml-2"
              >
                <ChevronLeft size={20} className="text-gray-500" />
              </button>
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <KeyRound size={20} className="text-orange-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-800">{t("settings.verification", "Verification")}</h2>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-4">
            <p className="text-center text-gray-600 text-sm">
              {t("settings.pour_reinitialiser_votre_pin_veuillez_vous_reconne", "To reset your PIN, please reconnect with your account")} {providerName}
            </p>

            {/* Error message */}
            {error && (
              <p className="text-center text-red-600 text-sm font-medium">
                {error}
              </p>
            )}

            <button
              onClick={handleOAuthReauth}
              disabled={oauthLoading}
              className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {oauthLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-700"></div>
              ) : (
                <>
                  {providerIcon}
                  {t("settings.continuer_avec", "Continuer avec")} {providerName}
                </>
              )}
            </button>

            <p className="text-center text-xs text-gray-500">
              {t("settings.vous_serez_redirige_pour_confirmer_votre_identite", "You will be redirected to confirm your identity")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Password verification screen
  if (showPasswordVerification) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setShowPasswordVerification(false);
                  setPassword('');
                  setError(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors -ml-2"
              >
                <ChevronLeft size={20} className="text-gray-500" />
              </button>
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <KeyRound size={20} className="text-orange-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-800">{t("settings.verification", "Verification")}</h2>
            </div>
          </div>

          {/* Content */}
          <form onSubmit={handlePasswordVerification} className="space-y-4">
            <p className="text-center text-gray-600 text-sm">
              {t("settings.entrez_le_mot_de_passe_de_votre_compte_pour_reinit", "Enter your account password to reset the PIN")}
            </p>

            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("settings.mot_de_passe_du_compte", "Mot de passe du compte")}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                autoFocus
                disabled={verifyingPassword}
              />
            </div>

            {/* Error message */}
            {error && (
              <p className="text-center text-red-600 text-sm font-medium">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={!password || verifyingPassword}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {verifyingPassword ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  {t("settings.verification", "Verifying...")}
                </>
              ) : (
                t("settings.verifier", "Verify")
              )}
            </button>

            {/* Forgot password link */}
            <div className="pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={handleForgotPassword}
                className="w-full text-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
              >
                {t("settings.forgot_password", "Forgot password?")}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Lock size={20} className="text-blue-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-800">{getTitle()}</h2>
          </div>
          {!required && onCancel && !passwordVerified && (
            <button
              onClick={onCancel}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="space-y-6">
          <p className="text-center text-gray-600">
            {getSubtitle()}
          </p>

          {/* First PIN entry */}
          {step === 'enter' && renderPinInputs(pin, inputRefs, false)}

          {/* Confirmation PIN entry */}
          {step === 'confirm' && (
            <>
              {/* Show first PIN as dots */}
              <div className="flex justify-center gap-3 mb-4">
                {pin.map((_, index) => (
                  <div
                    key={index}
                    className="w-14 h-14 border-2 border-green-300 bg-green-50 rounded-lg flex items-center justify-center"
                  >
                    <Check size={24} className="text-green-500" />
                  </div>
                ))}
              </div>
              {renderPinInputs(confirmPin, confirmInputRefs, true)}
            </>
          )}

          {/* Error message */}
          {error && (
            <p className="text-center text-red-600 text-sm font-medium">
              {error}
            </p>
          )}

          {/* Loading indicator */}
          {loading && (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            </div>
          )}

          {/* Info text */}
          {mode !== 'verify' && step === 'enter' && (
            <p className="text-center text-xs text-gray-500">
              {t("pin.this_code_protects_access_to_settings_an", "This code protects access to settings and purchases")}
            </p>
          )}

          {/* Forgot PIN link - only in verify mode when not already resetting */}
          {mode === 'verify' && userEmail && !passwordVerified && !showPasswordVerification && (
            <div className="pt-2 border-t border-gray-100">
              <button
                onClick={handleForgotPin}
                className="w-full text-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
              >
                {t("settings.forgot_pin", "Forgot PIN?")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentalPin;
